// Visitor-facing variant switcher for the scroll-flight A/B pair: a Dawn/Night
// segmented control in the engine's topbar. Switching is a full document
// navigation (the engine has no unmount API), carrying the current scroll
// fraction as ?at= so the visitor lands at the same waypoint in the other world.
// A switch is recorded as a preference signal and becomes the sticky variant.

import { isModifiedClick, persistVariant, trackFlightEvent } from '../flight-analytics';

export type FlightVariant = 'a' | 'b';

const VARIANTS: Record<FlightVariant, { label: string; path: string; title: string }> = {
  a: { label: 'Dawn', path: '/flight-a', title: 'The Quiet Floors — dawn ascent' },
  b: { label: 'Night', path: '/flight-b', title: 'Night Shift — the unseen team' },
};

export function installVariantToggle(container: HTMLElement, current: FlightVariant): void {
  const topbar = container.querySelector('.sw-topbar');
  const cta = container.querySelector('.sw-topcta');
  if (!topbar || !cta) return;

  const toggle = document.createElement('div');
  toggle.className = 'sw-vt';
  toggle.setAttribute('role', 'group');
  toggle.setAttribute('aria-label', 'Choose a flight');

  (Object.keys(VARIANTS) as FlightVariant[]).forEach((key) => {
    const v = VARIANTS[key];
    const link = document.createElement('a');
    link.textContent = v.label;
    link.title = v.title;
    link.href = v.path;
    if (key === current) {
      link.className = 'is-active';
      link.setAttribute('aria-current', 'page');
    } else {
      // Compute the scroll fraction at click time so the other world resumes
      // at the same waypoint, carrying utm_* through for audience segmentation.
      // A plain left-click is a direct preference signal: persist it as the
      // sticky variant and await the (capped) toggle event before navigating —
      // an unawaited fetch would be aborted by the full-document navigation.
      // Modified clicks (new tab/window) keep native behavior and are not
      // recorded as a preference — the current tab never switches arms.
      link.addEventListener('click', (e) => {
        const max = document.body.scrollHeight - window.innerHeight;
        const at = max > 0 ? window.scrollY / max : 0;
        const params = new URLSearchParams(window.location.search);
        params.delete('v');
        params.set('at', at.toFixed(4));
        const dest = `${v.path}?${params.toString()}`;
        link.href = dest;
        if (isModifiedClick(e)) return;
        e.preventDefault();
        persistVariant(key, 'choice');
        void trackFlightEvent(`/_e/flight/toggle/${current}-${key}`).then(() => {
          window.location.assign(dest);
        });
      });
    }
    toggle.appendChild(link);
  });

  // Group toggle + CTA on the right so the topbar keeps its three-part balance.
  const right = document.createElement('div');
  right.className = 'sw-topright';
  topbar.insertBefore(right, cta);
  right.appendChild(toggle);
  right.appendChild(cta);
}

export function restoreFlightPosition(): void {
  const at = new URLSearchParams(window.location.search).get('at');
  if (!at) return;
  const fraction = Math.min(1, Math.max(0, Number.parseFloat(at)));
  if (!Number.isFinite(fraction)) return;
  // Two frames: the engine's layout() has set the track height by then.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const max = document.body.scrollHeight - window.innerHeight;
      window.scrollTo({ top: fraction * max, behavior: 'instant' as ScrollBehavior });
    });
  });
}
